// PublicReviewPage.jsx — the page a client lands on from their review email.
// No login: the secret token in the link identifies the trip. The trip details (name, email,
// destination, service type, dates) come from the CRM and are locked. The client answers one
// question per page ("Next Page"), then sees a thank-you page with the review/website buttons
// from the email. Opened from:  <CRM address>/?review=<token>
// (/review/<token> works too where the host serves index.html for every path).
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { BRAND } from "../brand";
import { BRAND_HEX } from "../theme";
import { AnswerInput, LockedDetails, ReviewQuestions, errMsg, fmtDay, isShown, tint } from "./reviewShared";

/** The review token in the current URL, or null when this isn't a review link. */
export function getReviewToken() {
  try {
    const q = new URLSearchParams(window.location.search).get("review");
    if (q) return q.trim();
    const m = window.location.pathname.match(/\/review\/([A-Za-z0-9_-]{16,100})\/?$/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

const CHECK = "M5 13l4 4L19 7";
const CHEVRON_R = "M9 5l7 7-7 7";
const CHEVRON_L = "M15 19l-7-7 7-7";
const empty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length);

export default function PublicReviewPage({ token }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    api.get(`/public/reviews/${encodeURIComponent(token)}`)
      .then((data) => alive && setState({ loading: false, error: null, data }))
      .catch((e) => alive && setState({ loading: false, error: errMsg(e), data: null }));
    return () => { alive = false; };
  }, [token]);

  const data = state.data;
  const brandName = data?.brandName || BRAND.shortName || BRAND.name;
  useEffect(() => { document.title = `${brandName} · Share your feedback`; }, [brandName]);

  const submit = (answers) => api.post(`/public/reviews/${encodeURIComponent(token)}`, { answers });

  return (
    <div className="min-h-screen bg-white sm:bg-slate-50 sm:py-10">
      <div className="max-w-xl mx-auto bg-white sm:rounded-2xl sm:border sm:border-slate-100 sm:shadow-sm px-5 sm:px-10 py-8 sm:py-10 min-h-screen sm:min-h-0">
        {state.loading && (
          <div className="py-24 flex justify-center">
            <div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${BRAND_HEX} transparent transparent transparent` }} />
          </div>
        )}
        {state.error && (
          <div className="py-16 text-center">
            <p className="text-xl font-bold text-slate-800 mb-2">We couldn't open this review</p>
            <p className="text-sm text-slate-500">{state.error}</p>
            <p className="text-sm text-slate-400 mt-3">Please use the link in your most recent email from {brandName}, or reply to that email and we'll help.</p>
          </div>
        )}
        {data && <StepForm data={data} onSubmit={submit} />}
      </div>
      <p className="text-center text-xs text-slate-400 py-6">© {new Date().getFullYear()} {BRAND.name}</p>
    </div>
  );
}

/**
 * The step-by-step form. Used by the client's page and by the admin preview
 * (pass `preview` to skip saving).
 */
export function StepForm({ data: initial, onSubmit, preview = false }) {
  const [data, setData] = useState(initial);
  const [values, setValues] = useState({});
  const [step, setStep] = useState("intro"); // "intro" | question id | "done"
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const color = data.brandColor || BRAND_HEX;
  const brandName = data.brandName || BRAND.shortName;

  // Questions the client will actually see, given their answers so far.
  const visible = useMemo(() => {
    const seen = {};
    return (data.questions || []).filter((q) => {
      const show = isShown(q, seen);
      seen[q.id] = show ? values[q.id] : null;
      return show;
    });
  }, [data.questions, values]);

  const index = visible.findIndex((q) => q.id === step);
  const q = index === -1 ? null : visible[index];
  const isLast = index === visible.length - 1;

  const go = (next) => {
    setError(null);
    setStep(next);
    window.scrollTo?.({ top: 0 });
  };

  const next = async () => {
    if (step === "intro") return go(visible[0]?.id || "intro");
    if (!q) return;
    if (q.required && empty(values[q.id])) return setError("Please answer this question to continue.");
    if (!isLast) return go(visible[index + 1].id);
    // Last question → submit only what was shown.
    const answers = {};
    visible.forEach((v) => { if (!empty(values[v.id])) answers[v.id] = values[v.id]; });
    setSending(true);
    setError(null);
    try {
      if (preview) {
        setData((d) => ({ ...d, submitted: true, questions: d.questions.map((x) => ({ ...x, value: answers[x.id] ?? null })) }));
      } else {
        setData(await onSubmit(answers));
      }
      setJustSubmitted(true);
      go("done");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  const back = () => go(index <= 0 ? "intro" : visible[index - 1].id);

  // Enter = Next (except inside a paragraph answer).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || e.target?.tagName === "TEXTAREA" || data.submitted) return;
      if (e.target?.tagName === "BUTTON") return;
      e.preventDefault();
      next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const header = (
    <div className="mb-7">
      <div className="flex justify-center mb-6">
        {data.logoUrl ? (
          <img src={data.logoUrl} alt={brandName} className="max-h-14" />
        ) : (
          <span className="text-2xl font-bold tracking-tight" style={{ color, fontFamily: "var(--font-heading)" }}>{brandName}</span>
        )}
      </div>
      <div className="h-[3px] rounded-full bg-slate-200" />
    </div>
  );

  // ── Already submitted / just submitted ──
  if (data.submitted) {
    return (
      <div>
        {header}
        {data.isTest && <TestBanner />}
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: tint(color, 0.1) }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d={CHECK} />
            </svg>
          </div>
          <p className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
            {justSubmitted ? "Thank you!" : "You've already shared your feedback"}
          </p>
          <p className="text-[15px] text-slate-500 max-w-md mx-auto leading-relaxed">
            {justSubmitted
              ? data.thankYouMessage || "Your feedback has been received."
              : `We received it on ${fmtDay(data.submittedAt)}. Thank you!`}
          </p>
        </div>
        {data.links?.length > 0 && (
          <div className="flex flex-col gap-2.5 max-w-sm mx-auto mt-6">
            {data.links.map((l) => (
              <a key={l.url} href={l.url} target="_blank" rel="noreferrer"
                className="block text-center px-5 py-3.5 rounded-full border-2 font-semibold text-[15px] transition-colors"
                style={{ borderColor: color, color }}>
                {l.label}
              </a>
            ))}
          </div>
        )}
        {!justSubmitted && (
          <details className="mt-10 group">
            <summary className="cursor-pointer text-sm font-semibold text-slate-500 select-none">See your answers</summary>
            <div className="mt-5"><ReviewQuestions questions={data.questions} /></div>
          </details>
        )}
      </div>
    );
  }

  // ── Intro: greeting + locked trip details ──
  if (step === "intro" || !q) {
    return (
      <div>
        {header}
        {data.isTest && <TestBanner />}
        <p className="text-[26px] sm:text-[30px] leading-tight text-slate-800 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
          {data.title || "Share your feedback"}
        </p>
        {data.intro && <p className="text-[15px] text-slate-500 leading-relaxed mb-7">{data.intro}</p>}
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Your trip</p>
        <LockedDetails locked={data.locked} />
        <p className="text-xs text-slate-400 mt-3">These details come from our records. If anything is wrong, just reply to our email.</p>
        <div className="mt-8">
          <NextButton color={color} onClick={next}>Start</NextButton>
          <p className="text-xs text-slate-400 mt-3">{visible.length} short questions · about 2 minutes</p>
        </div>
      </div>
    );
  }

  // ── One question ──
  const pct = Math.round(((index + 1) / visible.length) * 100);
  return (
    <div>
      {header}
      {data.isTest && <TestBanner />}
      <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
        <span>Question {index + 1} of {visible.length}</span>
        <span className="truncate ml-3">{[data.locked?.destination, data.locked?.serviceType].filter(Boolean).join(" · ")}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-8">
        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, background: color }} />
      </div>

      <p className="text-[22px] sm:text-[26px] leading-snug text-slate-800 mb-2">
        {q.label}
        {!q.required && <span className="ml-2 text-sm text-slate-400 align-middle">(optional)</span>}
      </p>
      {q.helpText && <p className="text-sm text-slate-400 mb-2">{q.helpText}</p>}

      <div className="mt-6 mb-8" key={q.id}>
        <AnswerInput q={q} value={values[q.id]} large color={color}
          onChange={(v) => { setValues((p) => ({ ...p, [q.id]: v })); setError(null); }} />
      </div>

      {error && <p className="mb-4 text-sm text-rose-500">{error}</p>}

      <div className="flex items-center gap-4">
        <NextButton color={color} onClick={next} loading={sending}>
          {isLast ? (preview ? "Submit (preview)" : "Submit feedback") : "Next Page"}
        </NextButton>
        <button type="button" onClick={back} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-400 hover:text-slate-600">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d={CHEVRON_L} /></svg>
          Back
        </button>
      </div>
    </div>
  );
}

function NextButton({ color, onClick, loading, children }) {
  return (
    <button type="button" onClick={onClick} disabled={loading}
      className="inline-flex items-center justify-between gap-6 min-w-[11rem] px-6 py-3.5 rounded-lg text-white text-[17px] font-medium transition-all active:scale-[.98] disabled:opacity-60"
      style={{ background: color }}>
      <span>{children}</span>
      {loading
        ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d={CHEVRON_R} /></svg>}
    </button>
  );
}

function TestBanner() {
  return (
    <div className="mb-6 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-700">
      Test review — answers are saved as a test and never count as a client's review.
    </div>
  );
}
